import { findSiblings } from 'components/WorkspaceEditor/features/promoteDemote/utils/nodeHelpers'
import { AddPropertyErrorMessage } from 'pages/ProjectsPage/WorkspacePage/components/Common/addUtils'
import type {
    ValidationContext,
    ValidationResult,
    ValidationStrategy,
} from '../types'
import {
    extractParagraphKeyFromText,
    isDuplicateArticle,
    isDuplicateParagraphKey,
} from '../utils/validationHelpers'

const VALID: ValidationResult = { isValid: true }

/**
 * Validates duplicate article names. Self-gating: skips non-Article nodes.
 */
export const articleDuplicateValidator: ValidationStrategy = {
    validate: (context: ValidationContext): ValidationResult => {
        const {
            text,
            textElement,
            isAddOperation,
            contentNodes = [],
            nodeType,
        } = context

        if (nodeType !== 'Article') return VALID

        const filteredNodes = isAddOperation
            ? contentNodes
            : contentNodes.filter((node) =>
                  node.elements?.every((e) => e.id !== textElement.id)
              )

        const isDuplicate = isDuplicateArticle(text, filteredNodes)

        return isDuplicate
            ? {
                  isValid: false,
                  errorMessage: `Duplicate Article: "${text}" already exists. Please choose a different name.`,
              }
            : VALID
    },
}

/**
 * Validates duplicate paragraph keys at the same level. Self-gating: skips
 * non-OM workspaces and text without a key (no colon).
 */
export const paragraphKeyDuplicateValidator: ValidationStrategy = {
    validate: (context: ValidationContext): ValidationResult => {
        const {
            text,
            textElement,
            isAddOperation,
            contentNodes = [],
            parentNode,
            isContentTeamFromStudioPage,
        } = context

        if (!isContentTeamFromStudioPage) return VALID
        if (!parentNode) return VALID

        const keyFromText = extractParagraphKeyFromText(text)
        if (!keyFromText) return VALID

        const excludeId = isAddOperation
            ? undefined
            : contentNodes.find((node) =>
                  node.elements?.some((e) => e.id === textElement.id)
              )?.id

        const siblings = findSiblings(contentNodes, parentNode, excludeId)
        const isDuplicate = isDuplicateParagraphKey(keyFromText, siblings)

        return isDuplicate
            ? {
                  isValid: false,
                  errorMessage: AddPropertyErrorMessage,
              }
            : VALID
    },
}
